import { useEffect, useState } from "react";
import { getTmaStartParams } from "./tmaStartParams";
import { TelegramContextProvider } from "@/contexts/TelegramContext";
import { clientConfig } from "@/lib/config/client-config";
import { startTelegramAnalyticsOnConsent } from "@/lib/consent/telegram-analytics";
import { mixpanelClient } from "@/lib/mixpanel";
import * as Sentry from "@sentry/nextjs";
import {
  isTMA as isTelegramMiniApp,
  init,
  postEvent,
  backButton,
  setMiniAppHeaderColor,
  setMiniAppBackgroundColor,
  setMiniAppBottomBarColor,
  miniApp,
  RGB,
  retrieveRawInitData,
  retrieveLaunchParams,
} from "@telegram-apps/sdk-react";

export const TelegramProvider = ({ children }: { children: React.ReactNode }) => {
  const [isTMA, setIsTMA] = useState(false);
  const [initDataRaw, setInitDataRaw] = useState<string | null>(null);
  const [user, setUser] = useState<{ id: number; username: string; firstName: string } | null>(null);
  const [hasNavigated, setHasNavigated] = useState(false);

  useEffect(() => {
    const launchTMA = async () => {
      const isTelegramMiniAppValue = await isTelegramMiniApp("complete", { timeout: 100 });

      if (isTelegramMiniAppValue) {
        init();

        try {
          const launchParams = retrieveLaunchParams();
          if (launchParams?.tgWebAppData?.user) {
            setUser({
              id: launchParams.tgWebAppData.user.id,
              username: launchParams.tgWebAppData.user.username || "",
              firstName: launchParams.tgWebAppData.user.first_name,
            });
          }
          setIsTMA(true);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("Failed to retrieve TMA user:", error);
          Sentry.captureException(error);
          // Return early to not mount any buttons and not complete all the other logic
          return;
        }

        // Extract initDataRaw for TMA authentication
        try {
          const initDataRaw = retrieveRawInitData();
          if (initDataRaw) {
            setInitDataRaw(initDataRaw);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("Failed to retrieve TMA initDataRaw:", error);
          Sentry.captureException(error);
        }

        startTelegramAnalyticsOnConsent();

        postEvent("web_app_expand"); // Expand the web app to the full height of the screen

        backButton.mount();

        if (!miniApp.isMounted() && !miniApp.isMounting()) {
          await miniApp.mount();
          setMiniAppBackgroundColor("#151515" as RGB);
          setMiniAppHeaderColor("#151515" as RGB);
          setMiniAppBottomBarColor("#151515" as RGB);
        }

        // Handle TMA navigation immediately after initialization
        if (!hasNavigated) {
          try {
            const startParams = getTmaStartParams();
            if (startParams?.route) {
              const route = startParams.route;

              // Don't navigate if already on the target route (prevents infinite loop)
              if (window.location.pathname === route) {
                return;
              }

              setHasNavigated(true);

              window.location.href = route;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("TMA Provider: Navigation failed:", error);
            Sentry.captureException(error);
          }
        }

        mixpanelClient.registerPlatform(isTelegramMiniAppValue);
      } else if (clientConfig.tmaDev) {
        setIsTMA(true);
        setInitDataRaw("test");
        setUser({
          id: 1234567890,
          username: "test",
          firstName: "test",
        });
      }
    };

    launchTMA();
  }, []);

  return (
    <TelegramContextProvider isTMA={isTMA} initDataRaw={initDataRaw} user={user}>
      {children}
    </TelegramContextProvider>
  );
};
